import { Card, CardHeader, CardContent, Box, Typography, Skeleton, Chip } from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';

interface RevenueDataPoint {
  month: string;
  revenue: number;
}

interface RevenueChartProps {
  data: RevenueDataPoint[];
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
}

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const isPeak = payload[0].payload.isPeak;
    const isLow = payload[0].payload.isLow;

    return (
      <Box
        sx={{
          bgcolor: 'rgba(31, 41, 55, 0.95)',
          backdropFilter: 'blur(10px)',
          color: 'white',
          p: 2,
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          border: `1px solid ${isPeak ? '#10b981' : isLow ? '#ef4444' : 'rgba(108, 99, 255, 0.3)'}`,
        }}
      >
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
          {payload[0].payload.month}
          {isPeak && ' 🏆'}
          {isLow && ' 📉'}
        </Typography>
        <Typography variant="h6" sx={{ fontWeight: 700, color: isPeak ? '#10b981' : isLow ? '#ef4444' : '#6c63ff' }}>
          KES {payload[0].value?.toLocaleString()}
        </Typography>
        {isPeak && (
          <Typography variant="caption" sx={{ color: '#10b981', display: 'block', mt: 0.5 }}>
            Highest Revenue
          </Typography>
        )}
        {isLow && (
          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block', mt: 0.5 }}>
            Lowest Revenue
          </Typography>
        )}
      </Box>
    );
  }
  return null;
};

const CustomDot = (props: any) => {
  const { cx, cy, payload } = props;

  if (payload.isPeak) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill="#10b981" stroke="#fff" strokeWidth={3} />
        <circle cx={cx} cy={cy} r={12} fill="none" stroke="#10b981" strokeWidth={2} opacity={0.3} />
      </g>
    );
  }

  if (payload.isLow) {
    return (
      <g>
        <circle cx={cx} cy={cy} r={8} fill="#ef4444" stroke="#fff" strokeWidth={3} />
        <circle cx={cx} cy={cy} r={12} fill="none" stroke="#ef4444" strokeWidth={2} opacity={0.3} />
      </g>
    );
  }

  return (
    <circle cx={cx} cy={cy} r={5} fill="#fff" stroke="#6c63ff" strokeWidth={3} />
  );
};

export const RevenueChart = ({
  data,
  isLoading = false,
  title = 'Revenue Overview',
  subtitle = 'Monthly financial performance',
}: RevenueChartProps) => {
  // Default data with 12 months showing zero revenue
  const defaultData: RevenueDataPoint[] = [
    { month: 'Jan', revenue: 0 },
    { month: 'Feb', revenue: 0 },
    { month: 'Mar', revenue: 0 },
    { month: 'Apr', revenue: 0 },
    { month: 'May', revenue: 0 },
    { month: 'Jun', revenue: 0 },
    { month: 'Jul', revenue: 0 },
    { month: 'Aug', revenue: 0 },
    { month: 'Sep', revenue: 0 },
    { month: 'Oct', revenue: 0 },
    { month: 'Nov', revenue: 0 },
    { month: 'Dec', revenue: 0 },
  ];

  // Use provided data or default to zero data
  const sourceData = data && data.length > 0 ? data : defaultData;

  // Calculate statistics
  const revenues = sourceData.map(item => item.revenue);
  const totalRevenue = revenues.reduce((sum, val) => sum + val, 0);
  const averageRevenue = revenues.length > 0 ? totalRevenue / revenues.length : 0;
  const maxRevenue = Math.max(...revenues, 0);
  const minRevenue = Math.min(...revenues.filter(r => r > 0), maxRevenue);

  // Find peak and low months (only if there's variation in data)
  const hasVariation = maxRevenue !== minRevenue;
  const peakIndex = hasVariation ? revenues.indexOf(maxRevenue) : -1;
  const lowIndex = hasVariation && minRevenue > 0 ? revenues.indexOf(minRevenue) : -1;

  // Calculate growth trend
  const firstNonZero = revenues.find(r => r > 0) || 0;
  const lastNonZero = [...revenues].reverse().find(r => r > 0) || 0;
  const growthPercentage = firstNonZero > 0 ? ((lastNonZero - firstNonZero) / firstNonZero) * 100 : 0;
  const isGrowth = growthPercentage > 5;
  const isDecline = growthPercentage < -5;

  // Enhance chart data with indicators
  const chartData = sourceData.map((item, index) => ({
    ...item,
    isPeak: index === peakIndex,
    isLow: index === lowIndex,
  }));

  if (isLoading) {
    return (
      <Card
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.200',
          height: '100%',
        }}
      >
        <CardHeader
          title={<Skeleton width={150} />}
          subheader={<Skeleton width={100} />}
          sx={{
            '& .MuiCardHeader-title': {
              fontWeight: 600,
              fontSize: '1.125rem',
            },
          }}
        />
        <CardContent sx={{ pt: 2 }}>
          <Skeleton variant="rectangular" height={300} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        height: '100%',
        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
        boxShadow: '0 2px 8px rgba(108, 99, 255, 0.08)',
        transition: 'all 0.3s ease',
        animation: 'slideInUp 0.5s ease-out',
        '@keyframes slideInUp': {
          from: {
            opacity: 0,
            transform: 'translateY(20px)',
          },
          to: {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
        '&:hover': {
          boxShadow: '0 8px 24px rgba(108, 99, 255, 0.15)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <TrendingUp sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', color: '#1f2937' }}>
                  {title}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                  {subtitle}
                </Typography>
              </Box>
            </Box>
            {totalRevenue > 0 && (
              <Chip
                icon={
                  isGrowth ? (
                    <TrendingUp sx={{ fontSize: 18 }} />
                  ) : isDecline ? (
                    <TrendingDown sx={{ fontSize: 18 }} />
                  ) : (
                    <TrendingFlat sx={{ fontSize: 18 }} />
                  )
                }
                label={`${growthPercentage > 0 ? '+' : ''}${growthPercentage.toFixed(1)}%`}
                size="small"
                sx={{
                  bgcolor: isGrowth ? 'rgba(16, 185, 129, 0.1)' : isDecline ? 'rgba(239, 68, 68, 0.1)' : 'rgba(148, 163, 184, 0.1)',
                  color: isGrowth ? '#10b981' : isDecline ? '#ef4444' : '#94a3b8',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  '& .MuiChip-icon': {
                    color: isGrowth ? '#10b981' : isDecline ? '#ef4444' : '#94a3b8',
                  },
                }}
              />
            )}
          </Box>
        }
        sx={{ pb: 1 }}
      />
      <CardContent sx={{ pt: 0 }}>
        {totalRevenue > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ p: 2, bgcolor: 'rgba(108, 99, 255, 0.06)', borderRadius: 2, mb: 1.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                Total Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#6c63ff' }}>
                KES {totalRevenue.toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 140,
                  p: 1.5,
                  bgcolor: 'rgba(148, 163, 184, 0.06)',
                  borderRadius: 1.5,
                  border: '1px solid rgba(148, 163, 184, 0.1)',
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6875rem' }}>
                  Average
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b', mt: 0.5 }}>
                  KES {averageRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </Typography>
              </Box>
              {peakIndex >= 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 140,
                    p: 1.5,
                    bgcolor: 'rgba(16, 185, 129, 0.06)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(16, 185, 129, 0.1)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6875rem' }}>
                    Peak 🏆
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981', mt: 0.5 }}>
                    KES {maxRevenue.toLocaleString()}
                  </Typography>
                </Box>
              )}
              {lowIndex >= 0 && (
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 140,
                    p: 1.5,
                    bgcolor: 'rgba(239, 68, 68, 0.06)',
                    borderRadius: 1.5,
                    border: '1px solid rgba(239, 68, 68, 0.1)',
                  }}
                >
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.6875rem' }}>
                    Lowest 📉
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444', mt: 0.5 }}>
                    KES {minRevenue.toLocaleString()}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
          >
            <defs>
              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6c63ff" stopOpacity={0.4} />
                <stop offset="50%" stopColor="#667eea" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#764ba2" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="month"
              stroke="#94a3b8"
              style={{ fontSize: '0.8125rem', fontWeight: 500 }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
            />
            <YAxis
              stroke="#94a3b8"
              style={{ fontSize: '0.8125rem', fontWeight: 500 }}
              tickFormatter={(value) => {
                if (value === 0) return '0';
                if (value < 1000) return value.toString();
                if (value < 1000000) return `${(value / 1000).toFixed(1)}k`;
                return `${(value / 1000000).toFixed(1)}M`;
              }}
              tickLine={false}
              axisLine={{ stroke: '#e5e7eb' }}
              domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#6c63ff', strokeWidth: 1, strokeDasharray: '5 5' }} />
            {averageRevenue > 0 && (
              <ReferenceLine
                y={averageRevenue}
                stroke="#94a3b8"
                strokeDasharray="5 5"
                strokeWidth={2}
                label={{
                  value: 'Avg',
                  position: 'right',
                  fill: '#64748b',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="revenue"
              stroke="#6c63ff"
              strokeWidth={3}
              fill="url(#colorRevenue)"
              dot={<CustomDot />}
              activeDot={{
                fill: '#6c63ff',
                stroke: '#fff',
                strokeWidth: 3,
                r: 7,
              }}
              animationDuration={1000}
              animationBegin={200}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};
